import { courses } from './courses';
import { projects } from './projects';
import type { LocaleCode } from './types';

/**
 * URL canônica do site. Usada em Open Graph, Twitter Card, sitemap e JSON-LD.
 */
export const SITE_URL = 'https://targas.dev';

/** Caminho da foto usada nos previews de redes sociais. */
export const SITE_OG_IMAGE = '/images/profile.jpg';

/** Rotas públicas indexáveis (path relativo, sem trailing slash). */
export const PUBLIC_ROUTES = ['/'] as const;

/** Categorias agrupadas — usadas no <ul class="sr-only"> da seção Sobre. */
export const skillKeywords: Record<string, string[]> = {
  frontend: [
    'HTML5', 'CSS3', 'SASS', 'LESS', 'Tailwind CSS', 'Bootstrap',
    'JavaScript', 'ES6+', 'TypeScript',
    'Vue.js', 'Vue 2', 'Vue 3', 'Pinia', 'Vuex', 'Vue Router', 'Nuxt.js',
    'React', 'Redux', 'Next.js',
    'Angular', 'jQuery', 'Web Components',
    'Acessibilidade', 'WCAG', 'Performance Web', 'Core Web Vitals',
    'Responsive Design', 'PWA', 'SEO técnico',
  ],
  backend: [
    'Node.js', 'Express', 'PHP', 'Laravel',
    'REST APIs', 'GraphQL',
    'MySQL', 'PostgreSQL', 'DynamoDB', 'MongoDB',
  ],
  cloud: [
    'AWS', 'Amazon Web Services', 'EC2', 'S3', 'Lambda',
    'Serverless Framework', 'Docker', 'CI/CD', 'GitHub Actions',
    'Vercel', 'Netlify',
  ],
  mobileDesktop: ['Ionic', 'Capacitor', 'Tauri'],
  quality: [
    'Jest', 'Vitest', 'Cypress', 'Playwright',
    'ESLint', 'SonarQube', 'Quality Gate', 'Code Review',
    'Testes Unitários', 'TDD', 'Husky',
  ],
  tooling: [
    'Git', 'Git Flow', 'NPM', 'Yarn', 'pnpm',
    'Vite', 'Webpack', 'Babel', 'Husky',
  ],
  designUx: [
    'Figma', 'Adobe Photoshop', 'Adobe Illustrator', 'Adobe XD', 'Zeplin',
    'Design System', 'Prototipagem', 'UI Design',
  ],
  motionGames: [
    'Adobe Premiere', 'Adobe After Effects', 'Adobe Animate',
    'Godot', 'Unity', 'Motion Design', 'Game Development',
  ],
  workflow: [
    'Agile', 'Scrum', 'Kanban',
    'Harness Engineering', 'Spec-Driven Development', 'SDD',
    'Cursor', 'IA Generativa', 'Generative AI',
    'Mentoria técnica', 'Pair Programming',
  ],
};

/** Lista plana de todas as keywords (sem duplicados). */
export const allSkillKeywords: string[] = Array.from(
  new Set(Object.values(skillKeywords).flat()),
);

export type SeoPageKey = 'home' | 'about' | 'projects' | 'uses';

interface SeoCopy {
  title: string;
  description: string;
  jobTitle: string;
  intro: string;
}

const baseSeoCopy: Record<LocaleCode, SeoCopy> = {
  'pt-BR': {
    title: 'Danilo Targas | Desenvolvedor Front-end Sênior',
    description:
      'Desenvolvedor front-end sênior em São Paulo. Performance, qualidade, '
      + 'produtos de grande escala e entrega para marcas como iFood, Claro e UNICEF.',
    jobTitle: 'Desenvolvedor Front-end Sênior',
    intro:
      'Portfólio de Danilo Targas, desenvolvedor front-end sênior com mais de 10 anos '
      + 'de experiência em produtos digitais de grande escala.',
  },
  en: {
    title: 'Danilo Targas | Senior Front-end Developer',
    description:
      'Senior front-end developer in São Paulo. Performance, quality, '
      + 'large-scale products and delivery for brands such as iFood, Claro and UNICEF.',
    jobTitle: 'Senior Front-end Developer',
    intro:
      'Portfolio of Danilo Targas, senior front-end developer with 10+ years of '
      + 'experience on large-scale digital products.',
  },
};

const pageSeoOverrides: Record<Exclude<SeoPageKey, 'home'>, Record<LocaleCode, Pick<SeoCopy, 'title' | 'description'>>> = {
  about: {
    'pt-BR': {
      title: 'Sobre mim | Danilo Targas',
      description:
        'Conheça Danilo Targas, desenvolvedor front-end sênior em São Paulo. '
        + 'Design digital, produtos de grande escala, qualidade e Harness Engineering.',
    },
    en: {
      title: 'About me | Danilo Targas',
      description:
        'Meet Danilo Targas, senior front-end developer in São Paulo. '
        + 'Digital design background, large-scale products, quality and Harness Engineering.',
    },
  },
  projects: {
    'pt-BR': {
      title: 'Portfólio | Danilo Targas',
      description:
        'Projetos web de Danilo Targas para UNICEF, Porto Seguro, Tenda, Marfrig '
        + 'e outras marcas. Front-end, performance e qualidade.',
    },
    en: {
      title: 'Portfolio | Danilo Targas',
      description:
        'Web projects by Danilo Targas for UNICEF, Porto Seguro, Tenda, Marfrig '
        + 'and other brands. Front-end, performance and quality.',
    },
  },
  uses: {
    'pt-BR': {
      title: 'Uses | Danilo Targas',
      description:
        'Stack, ferramentas e setup de desenvolvimento de Danilo Targas. '
        + 'Front-end, qualidade e produtividade.',
    },
    en: {
      title: 'Uses | Danilo Targas',
      description:
        'Stack, tools and development setup of Danilo Targas. '
        + 'Front-end, quality and productivity.',
    },
  },
};

export function getSeoCopy(locale: LocaleCode, page: SeoPageKey = 'home'): SeoCopy {
  const base = baseSeoCopy[locale];
  if (page === 'home') return base;
  const override = pageSeoOverrides[page][locale];
  return { ...base, ...override };
}

export function buildCanonicalUrl(path: string): string {
  if (path === '/' || path === '') return `${SITE_URL}/`;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}

/** Monta JSON-LD schema.org/Person para o locale ativo. */
export function buildPersonStructuredData(locale: LocaleCode): Record<string, unknown> {
  const copy = getSeoCopy(locale);

  return {
    '@type': 'Person',
    name: 'Danilo Targas',
    givenName: 'Danilo',
    familyName: 'Targas',
    jobTitle: copy.jobTitle,
    description: copy.description,
    url: SITE_URL,
    image: `${SITE_URL}${SITE_OG_IMAGE}`,
    email: 'mailto:danilorodriguestargas@gmail.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'São Paulo',
      addressRegion: 'SP',
      addressCountry: 'BR',
    },
    knowsLanguage: ['pt-BR', 'en'],
    knowsAbout: allSkillKeywords,
    hasCredential: courses.map((c) => ({
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'course',
      name: c.i18n[locale].title,
      recognizedBy: {
        '@type': 'Organization',
        name: c.i18n[locale].institution,
      },
    })),
    sameAs: [
      'https://www.linkedin.com/in/danilo-targas-b749bb13b/',
      'https://codepen.io/targas',
    ],
  };
}

/** Projetos públicos como CreativeWork / SoftwareSourceCode no grafo JSON-LD. */
export function buildProjectStructuredData(locale: LocaleCode): Record<string, unknown>[] {
  return projects
    .filter((project) => project.link)
    .map((project) => {
      const copy = project.i18n[locale];
      return {
        '@type': 'SoftwareSourceCode',
        name: copy.title,
        description: copy.text,
        url: project.link,
        programmingLanguage: copy.technology.split('|').map((s) => s.trim()),
        author: {
          '@type': 'Person',
          name: 'Danilo Targas',
          url: SITE_URL,
        },
        image: `${SITE_URL}${project.image}`,
      };
    });
}

/** Grafo JSON-LD completo: Person + projetos indexáveis. */
export function buildStructuredDataGraph(
  locale: LocaleCode,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      buildPersonStructuredData(locale),
      ...buildProjectStructuredData(locale),
    ],
  };
}
